import { useEffect, useMemo, useRef, useState } from 'react'

type SkillListItem = {
  id: string
  name: string
  description?: string | null
  is_enabled?: boolean
}

export type SlashSkillMenuSelection = {
  skillId: string
  skillName: string
}

function getSlashQuery(value: string): { open: boolean; query: string } {
  // "Claude-like": open when the *current token* begins with /.
  // Supports multiple skills like: "/skill-a /skill-b write a prompt..."
  const t = value ?? ''
  const firstLine = t.split('\n')[0] ?? ''
  const parts = firstLine.split(' ').filter(Boolean)
  if (parts.length === 0) return { open: false, query: '' }
  const last = parts[parts.length - 1] ?? ''
  if (!last.startsWith('/')) return { open: false, query: '' }
  return { open: true, query: last.slice(1).trim().toLowerCase() }
}

export function SlashSkillsMenu({
  value,
  onPick,
  disabled,
  suppressed,
  excludeSkillIds,
}: {
  value: string
  onPick: (sel: SlashSkillMenuSelection) => void
  disabled?: boolean
  suppressed?: boolean
  excludeSkillIds?: string[]
}) {
  const { open, query } = useMemo(() => getSlashQuery(value), [value])

  const [skills, setSkills] = useState<SkillListItem[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadErr, setLoadErr] = useState<string | null>(null)

  const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

  useEffect(() => {
    if (!open || loaded || disabled) return
    let cancelled = false
    const run = async () => {
      try {
        setLoadErr(null)
        const res = await fetch(`${API_BASE}api/skills`, { credentials: 'include' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as { skills?: SkillListItem[] }
        const items = Array.isArray(data.skills) ? data.skills : []
        if (!cancelled) {
          setSkills(items)
          setLoaded(true)
        }
      } catch (e) {
        console.warn('Failed to load /api/skills for slash menu', e)
        if (!cancelled) {
          setLoadErr('Failed to load skills')
          setLoaded(true)
        }
      }
    }
    run()
    return () => { cancelled = true }
  }, [API_BASE, open, loaded, disabled])

  const filtered = useMemo(() => {
    const q = query.trim()
    if (!q) return skills
    return skills.filter(s => {
      const hay = `${s.name ?? ''} ${s.description ?? ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [skills, query])

  const enabledOnly = useMemo(() => filtered.filter(s => s.is_enabled !== false), [filtered])
  const excludeSet = useMemo(() => new Set((excludeSkillIds ?? []).filter(Boolean)), [excludeSkillIds])
  const visible = useMemo(() => enabledOnly.filter(s => !excludeSet.has(s.id)), [enabledOnly, excludeSet])

  if (!open || suppressed) return null

  return (
    <div
      role="listbox"
      aria-label="Skills"
      style={{
        position: 'absolute',
        left: 10,
        bottom: 'calc(100% + 8px)',
        width: 360,
        maxWidth: 'calc(100vw - 24px)',
        background: 'var(--bg, white)',
        border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        zIndex: 50,
      }}
    >
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, padding: '2px 4px 8px' }}>
          Skills
        </div>

        {loadErr ? (
          <div style={{ padding: '10px 6px', fontSize: 13, opacity: 0.8 }}>{loadErr}</div>
        ) : (!loaded ? (
          <div style={{ padding: '10px 6px', fontSize: 13, opacity: 0.8 }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '10px 6px', fontSize: 13, opacity: 0.8 }}>No skills found.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6, maxHeight: 280, overflow: 'auto', paddingRight: 2 }}>
            {visible.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onPick({ skillId: s.id, skillName: s.name })}
                style={{
                  textAlign: 'left',
                  border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
                  background: 'var(--bg, white)',
                  borderRadius: 10,
                  padding: '10px 10px',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 650, fontSize: 13 }}>{s.name}</div>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

